import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, CheckSquare, Square, XCircle } from 'lucide-react';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { StaffNames } from '../components/shared/NowName';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import { decisionLine, latestEventOf } from '../components/workflow/eventActor';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import EngagementRoster from '../components/workflow/EngagementRoster';
import { MultiStaffPicker } from '../components/shared/EngagementFormFields';
import { INDEPENDENCE_REMINDER } from '../components/workflow/IndependenceDeclaration';
import { EngagementName, withCoverageFmt } from '../components/workflow/CoverageDisplay';
import { engagementNo } from '../utils/engagementNo';
import { btnApprove, btnReject } from '../components/workflow/constants';
import { formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// CaseWare, Charge Code & Team Update — detail page (docs/modules/workflow-cwcc.md).
// TWO PHASES: a review-key holder approves the whole request (every requested
// side → approved), then QM performs each item in the external systems and
// marks it completed — only that completion writes the engagement flags/team.

// Named rather than inline: History and the "Changes since it was returned"
// card must name a field identically.
// engagementName 是單案時代的 diff key——舊 History 事件還讀得到。
const CWCC_CHANGE_LABELS = {
  engagementName: 'Engagement',
  engagementNames: 'Engagements',
  needCw: 'CaseWare Requested',
  needCc: 'Charge Code Requested',
  needMember: 'Team Member Update Requested',
  teamMembers: 'Requested Team Members',
  remarks: 'Remarks',
};
const fmtCwccChangeValue = withCoverageFmt(
  (field, v) => (v === null || v === undefined || v === '' ? '—' : String(v))
);

const SIDE_LABELS = {
  cw: 'CaseWare Access',
  cc: 'Charge Code',
  member: 'Team Member Update',
  // QM 執行段的兩顆——team 異動要在 CaseWare 與 charge code 各做一次。
  member_cw: 'Team Update in CaseWare',
  member_cc: 'Team Update for the Charge Code',
};
// The same sides as they read on the QM "Mark … Completed" buttons.
const COMPLETE_BUTTON_LABELS = {
  cw: 'CaseWare Access',
  cc: 'Charge Code',
  member: 'Team Member Update',
  member_cw: 'Team Update in CaseWare',
  member_cc: 'Team Update for the Charge Code',
};

// 「申請了什麼」逐項列——照 New 頁第 2 段的勾選清單長相：三個項目都列出來，
// 勾了的深色＋該側狀態章，沒勾的淺色空框，一眼對得回申請單。逐項意見入口
// 掛在每一列下（沒勾的也能留——審核者可能要求把某側也勾起來）。
const RequestedItemRow = ({ ticked, label, status, field, commentLabel, children }) => (
  <div className="group">
    <div className="flex items-center gap-2">
      {ticked
        ? <CheckSquare className="w-4 h-4 text-slate-700 shrink-0" />
        : <Square className="w-4 h-4 text-slate-300 shrink-0" />}
      <span className={`text-sm ${ticked ? 'text-slate-800' : 'text-slate-400'}`}>{label}</span>
      {ticked
        ? <WorkflowStatusBadge status={status} size="chip" />
        : <span className="text-xs text-slate-400">Not requested</span>}
    </div>
    <FieldCommentAnchor field={field} label={commentLabel} className="mt-1 ml-6" compact hoverOnly />
    {children && <div className="mt-2 ml-6 space-y-3">{children}</div>}
  </div>
);

const WorkflowCwccDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [canComplete, setCanComplete] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [engagementLock, setEngagementLock] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/cwcc/${id}`),
        fetchJSON(`/api/workflow/cwcc/${id}/events`),
      ]);
      setRequest(detail.request);
      setEngagements(detail.currentEngagements || []);
      setCanReview(detail.canReview);
      setCanComplete(detail.canComplete);
      setCanReject(detail.canReject);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEngagementLock(detail.engagementLock || null);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Phase 1 — a review-key holder approves the WHOLE request.
  const approve = async () => {
    setActing(true); setError(null);
    try {
      await fetchJSON(`/api/workflow/cwcc/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      setNotice('Approved — QM has been notified to process the requested item(s).');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  // Phase 2 — QM has performed one item; this writes the engagement.
  const complete = async (side) => {
    setActing(true); setError(null);
    try {
      const res = await fetchJSON(`/api/workflow/cwcc/${id}/complete`, {
        method: 'POST', body: JSON.stringify({ side }),
      });
      const after = res && res.request;
      setNotice(side === 'cw' ? 'CaseWare access completed — the engagement is marked as open.'
        : side === 'cc' ? 'Charge code completed — the engagement is marked as assigned.'
          : after && after.memberStatus === 'completed'
            ? 'Team update completed — the engagement team has been replaced.'
            : `Team update marked done ${side === 'member_cw' ? 'in CaseWare' : 'for the charge code'} — the team is replaced once the other one is done too.`);
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true); setError(null);
    try {
      await fetchJSON(`/api/workflow/cwcc/${id}/resubmit`, { method: 'POST' });
      setNotice('Re-submitted — the reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const sideStates = useMemo(() => (request ? [
    ['cw', request.cwStatus, request.cwStatusUpdatedAt],
    ['cc', request.ccStatus, request.ccStatusUpdatedAt],
    ['member', request.memberStatus, request.memberStatusUpdatedAt],
  ].filter(([, v]) => v && v !== 'na') : []), [request]);
  const anyRequested = sideStates.some(([, v]) => v === 'requested');
  const approvedSides = sideStates.filter(([, v]) => v === 'approved');
  const hasAwaitingInfo = sideStates.some(([, v]) => v === 'awaiting_info');

  // QM 的完成鈕：一側一顆，member 側兩顆（CaseWare 一顆、charge code 一顆），
  // 按過的那顆收起來。兩顆都按完 member 側才翻 completed、team 才替換。
  const completionKeys = useMemo(() => (request ? approvedSides.flatMap(([key]) => (
    key === 'member'
      ? [request.memberCwCompletedAt ? null : 'member_cw', request.memberCcCompletedAt ? null : 'member_cc'].filter(Boolean)
      : [key]
  )) : []), [request, approvedSides]);

  // member 側核准後的進度說明，讓看板上分得出「還差哪一邊」。
  const memberNote = (request && request.memberCwCompletedAt && !request.memberCcCompletedAt)
    ? 'Done in CaseWare — waiting for the charge code side'
    : (request && request.memberCcCompletedAt && !request.memberCwCompletedAt)
      ? 'Done for the charge code — waiting for CaseWare'
      : 'Waiting for QM in CaseWare and for the charge code';
  // 誰有審核鑰匙誰都能核准——核准者印在已核准那一側的最上面。
  const approvedBy = decisionLine('Approved', latestEventOf(events, ['approved']));

  // 退件的逐項意見：能退件的人（審核鑰匙持有者）就能逐格留言。
  const canComment = !!request && canReject;
  const fc = useFieldComments({
    events,
    canComment,
    returned: hasAwaitingInfo,
    labels: CWCC_CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/cwcc"
      backLabel="CaseWare, Charge Code & Team Update"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="CaseWare, Charge Code & Team Update"
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            lock={engagementLock}
            sides={sideStates.map(([key, status, at]) => ({
              key,
              label: SIDE_LABELS[key],
              status,
              note: status === 'approved'
                ? [approvedBy, key === 'member' ? memberNote : 'Waiting for QM to complete it'].filter(Boolean).join(' · ')
                : (status === 'completed' && decisionLine('Completed', latestEventOf(events, [`completed_${key}`])))
                  || (at ? formatDateTime(at) : null),
            }))}
            busy={acting}
            events={events}
            onReject={null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
            beforeActions={(canReview || (canComplete && completionKeys.length > 0) || (canReject && anyRequested)) ? (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                {canReview && anyRequested && (
                  <button type="button" onClick={approve} disabled={acting} className={btnApprove}>
                    <CheckCircle2 className="w-4 h-4" /> Approve Request
                  </button>
                )}
                {canComplete && completionKeys.map((key) => (
                  <button key={key} type="button" onClick={() => complete(key)} disabled={acting} className={btnApprove}>
                    <CheckCircle2 className="w-4 h-4" /> Mark {COMPLETE_BUTTON_LABELS[key] || SIDE_LABELS[key]} Completed
                  </button>
                ))}
                {/* 退件只在審核段——核准後 QM 是在執行，不是再判定一次。 */}
                {canReject && anyRequested && (
                  <button type="button" onClick={() => setRejecting(true)} disabled={acting} className={btnReject}>
                    <XCircle className="w-4 h-4" /> Return for Changes
                  </button>
                )}
              </div>
            ) : null}
          />

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CWCC_CHANGE_LABELS}
            fmtChangeValue={fmtCwccChangeValue}
          />

          {/* 三張卡照 New 頁的段落排：①選了哪些案（附各案現況）→
              ②申請了什麼（勾選清單原樣重現）→ ③聲明與備註。 */}
          <SectionCard title={(request.engagements || []).length > 1 ? 'Engagements' : 'Engagement'}>
            <div className="divide-y divide-slate-100">
              {(request.engagements || []).map((e) => {
                const live = engagements.find((x) => x.id === e.engagementId) || null;
                return (
                  <div key={e.engagementId || e.engagementName} className="py-2 first:pt-0 last:pb-0">
                    <div className="text-sm text-slate-800"><EngagementName name={e.engagementName} no={engagementNo(e.engagementId)} serviceDescription={e.serviceDescription} /></div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      CaseWare: {live ? (live.casewareOpened ? 'activated' : 'not activated') : '—'}
                      {' · '}Charge code: {live ? (live.chargeCodeAssigned ? 'activated' : 'not activated') : '—'}
                    </div>
                    {live && <EngagementRoster engagement={live} className="mt-2" />}
                  </div>
                );
              })}
            </div>
            <FieldCommentAnchor field="engagementNames" label="Engagements" />
          </SectionCard>

          <SectionCard title="Requested Items">
            <div className="space-y-3">
              <RequestedItemRow ticked={request.cwStatus !== 'na'} label={SIDE_LABELS.cw}
                status={request.cwStatus} field="needCw" commentLabel="CaseWare Requested" />
              <RequestedItemRow ticked={request.ccStatus !== 'na'} label={SIDE_LABELS.cc}
                status={request.ccStatus} field="needCc" commentLabel="Charge Code Requested" />
              <RequestedItemRow ticked={request.memberStatus !== 'na'} label={SIDE_LABELS.member}
                status={request.memberStatus} field="needMember" commentLabel="Team Member Update Requested">
                {request.memberStatus !== 'na' && (
                  <>
                    {/* 現任 team 在 Engagement 卡（每案都列），這裡只列申請後的名單。 */}
                    <DetailItem label="Requested Team Members" field="teamMembers">
                      {(request.teamMembers || []).length > 0
                        ? <StaffNames list={request.teamMembers} />
                        : '—'}
                    </DetailItem>
                    {/* QM 的兩個執行步驟——team 名單兩邊都做完才替換。 */}
                    {['requested', 'awaiting_info'].includes(request.memberStatus) ? null : (
                      <DetailGrid>
                        <DetailItem label={SIDE_LABELS.member_cw}>
                          {request.memberCwCompletedAt ? formatDateTime(request.memberCwCompletedAt) : 'Not done yet'}
                        </DetailItem>
                        <DetailItem label={SIDE_LABELS.member_cc}>
                          {request.memberCcCompletedAt ? formatDateTime(request.memberCcCompletedAt) : 'Not done yet'}
                        </DetailItem>
                      </DetailGrid>
                    )}
                  </>
                )}
              </RequestedItemRow>
            </div>
          </SectionCard>

          <SectionCard title="Declaration & Remarks">
            <DetailGrid>
              <DetailItem label="Independence Declaration">{request.independenceDeclared ? 'Confirmed by requester' : '—'}</DetailItem>
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Attachments">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/cwcc/${request.id}/files/${f.id}`}
            />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          <HistoryCard
            events={events}
            changeLabels={CWCC_CHANGE_LABELS}
            fmtChangeValue={fmtCwccChangeValue}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/cwcc/${request.id}/reject`, {
                  method: 'POST', body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <CwccModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo ? 'Changes saved. Press Re-submit to send the request back for review.' : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its files. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => { await fetchJSON(`/api/workflow/cwcc/${request.id}`, { method: 'DELETE' }); navigate('/workflow/cwcc'); }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const CwccModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [needCw, setNeedCw] = useState(request.cwStatus !== 'na');
  const [needCc, setNeedCc] = useState(request.ccStatus !== 'na');
  const [needMember, setNeedMember] = useState(request.memberStatus !== 'na');
  const [teamStaffIds, setTeamStaffIds] = useState(
    (request.teamMembers || []).map((m) => m.staffId).filter(Boolean)
  );
  const [staff, setStaff] = useState([]);
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { removeIds, toggle: toggleRemove, appendTo } = useRemovableFiles();

  const [feed, setFeed] = useState(null);
  const [engagementIds, setEngagementIds] = useState(
    (request.engagements || []).map((e) => e.engagementId).filter(Boolean)
  );
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/cwcc/options');
        setFeed(data.engagements || []);
        setStaff(data.staff || []);
      } catch { setFeed([]); }
    })();
  }, []);
  // 本單已涵蓋的案一定要在 feed 裡（缺席就補），picker 才畫得出已選 chips。
  const pickerFeed = useMemo(() => {
    if (!feed) return null;
    const have = new Set(feed.map((e) => e.id));
    const extras = (request.engagements || [])
      .filter((e) => e.engagementId && !have.has(e.engagementId))
      .map((e) => ({
        id: e.engagementId, engagementName: e.engagementName, entityName: e.engagementName,
        serviceProvided: e.serviceProvided, engagementSeq: e.engagementSeq, serviceDescription: e.serviceDescription,
      }));
    return [...feed, ...extras];
  }, [feed, request.engagements]);
  // A side a covered engagement already has cannot be newly asked for (the
  // server answers CW_ALREADY_OPEN / CC_ALREADY_ASSIGNED) — same rule as the
  // New form; a side this request already carries stays editable.
  const selectedLive = useMemo(
    () => (pickerFeed || []).filter((e) => engagementIds.includes(e.id)),
    [pickerFeed, engagementIds]
  );
  const cwBlocked = request.cwStatus === 'na' && selectedLive.some((e) => e.casewareOpened);
  const ccBlocked = request.ccStatus === 'na' && selectedLive.some((e) => !!e.chargeCodeAssigned);
  useEffect(() => { if (cwBlocked) setNeedCw(false); }, [cwBlocked]);
  useEffect(() => { if (ccBlocked) setNeedCc(false); }, [ccBlocked]);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Save the modal marks those controls and lists them in the banner.
  const gaps = useMemo(() => {
    const g = [];
    if (engagementIds.length === 0) g.push({ key: 'engagementNames', label: 'Engagement(s)' });
    if (!needCw && !needCc && !needMember) g.push({ key: 'requestedSides', label: 'CaseWare Access / Charge Code / Team Member Update' });
    if (needMember && teamStaffIds.length === 0) g.push({ key: 'teamMembers', label: 'Team Members (At Least One)' });
    return g;
  }, [engagementIds, needCw, needCc, needMember, teamStaffIds]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const form = new FormData();
      form.set('engagementIds', JSON.stringify(engagementIds));
      form.set('needCw', needCw ? 'true' : 'false');
      form.set('needCc', needCc ? 'true' : 'false');
      form.set('needMember', needMember ? 'true' : 'false');
      if (needMember) form.set('teamStaffIds', JSON.stringify(teamStaffIds));
      form.set('remarks', remarks);
      files.forEach((f) => form.append('files', f));
      appendTo(form);
      await fetchJSON(`/api/workflow/cwcc/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Engagement(s)" field="engagementNames">
          {pickerFeed === null ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <GroupedEngagementPicker
              engagements={pickerFeed}
              selectedIds={engagementIds}
              onChange={setEngagementIds}
            />
          )}
        </Field>
        {/* The checkbox trio is not a Field, so its "Required" mark sits right under it. */}
        <div className="space-y-2">
          <label className={`flex items-center gap-2 text-sm ${cwBlocked ? 'text-slate-400' : 'text-slate-700'}`}>
            <input type="checkbox" checked={needCw} disabled={cwBlocked} onChange={(e) => setNeedCw(e.target.checked)} />
            CaseWare Access {cwBlocked && <span className="text-xs">(already activated on a selected engagement)</span>}
          </label>
          <label className={`flex items-center gap-2 text-sm ${ccBlocked ? 'text-slate-400' : 'text-slate-700'}`}>
            <input type="checkbox" checked={needCc} disabled={ccBlocked} onChange={(e) => setNeedCc(e.target.checked)} />
            Charge Code {ccBlocked && <span className="text-xs">(already activated on a selected engagement)</span>}
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={needMember} onChange={(e) => setNeedMember(e.target.checked)} />
            Team Member Update
          </label>
          <FieldInvalidInline field="requestedSides" text="Required — tick at least one item" />
        </div>
        {needMember && (
          <Field label="Requested Team Members" required field="teamMembers"
            hint="The full team after the update, applied to every covered engagement when QM completes the item.">
            <MultiStaffPicker
              options={staff}
              known={request.teamMembers || []}
              selectedIds={teamStaffIds}
              onChange={setTeamStaffIds}
              placeholder="Add a team member…"
            />
          </Field>
        )}
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggleRemove}
          downloadHref={(f) => `${API_BASE_URL}/api/workflow/cwcc/${request.id}/files/${f.id}`}
        />
        <FieldCommentInline field="files" />
        <Field label="Add More Supporting Files (e.g. Booking Form)">
          <FilePicker multiple kind="docsAndImages"
            onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </Field>
        {/* 聲明在送出那一刻就記錄了，這裡不再重問（同 Signed Financial Statements）——但這張
            modal 改得動 team 名單，所以把聲明原句留在畫面上，提醒填單人他
            代表的是誰。 */}
        {request.independenceDeclared && (
          <Field label="Independence Declaration">
            <p className="text-xs text-slate-600 leading-relaxed border border-slate-200 rounded-lg p-3 bg-slate-50">
              {INDEPENDENCE_REMINDER}
              {' '}<span className="text-slate-500">Confirmed when this request was submitted; it covers everyone named on it.</span>
            </p>
          </Field>
        )}
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
        </Field>
      </FormValidationProvider>
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowCwccDetail;
